import type { BookSummary } from '../models';

// Client-side equivalent of the Drift FTS5 search (`books_fts MATCH
// '"<query>"*'`): a phrase-prefix match — the query's words appear in order
// in one field, the last word as a prefix — with unicode61-style tokens
// (case-folded, diacritics removed). Fields: title, author, narrators,
// series and description (HTML stripped).

/** Splits text into case-folded, accent-free word tokens. */
export function tokenize(text: string): string[] {
  return text
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;|&#\d+;/gi, ' ');

type FieldTokens = string[][];

// Rows from the library cache are stable objects, so tokens are computed once per row.
const tokenCache = new WeakMap<BookSummary, FieldTokens>();

function fieldTokens(book: BookSummary): FieldTokens {
  let tokens = tokenCache.get(book);
  if (!tokens) {
    tokens = [
      tokenize(book.title),
      tokenize(`${book.authorName} ${book.authors.map((a) => a.name).join(' ')}`),
      tokenize(book.narrators.join(' ')),
      tokenize(book.series.map((s) => s.name).join(' ')),
      tokenize(stripHtml(book.description)),
    ];
    tokenCache.set(book, tokens);
  }
  return tokens;
}

function phrasePrefixMatch(tokens: readonly string[], query: readonly string[]): boolean {
  const n = query.length;
  const last = query[n - 1] as string;
  for (let i = 0; i + n <= tokens.length; i++) {
    let ok = true;
    for (let j = 0; j < n - 1; j++) {
      if (tokens[i + j] !== query[j]) {
        ok = false;
        break;
      }
    }
    if (ok && (tokens[i + n - 1] as string).startsWith(last)) return true;
  }
  return false;
}

/** True when `book` matches `query` (an empty query matches everything). */
export function matchesQuery(book: BookSummary, query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed) return true;
  const q = tokenize(trimmed);
  if (q.length === 0) {
    // FTS rejects punctuation-only input; Flutter then falls back to LIKE on title/author.
    const needle = trimmed.toLowerCase();
    return book.title.toLowerCase().includes(needle) || book.authorName.toLowerCase().includes(needle);
  }
  return fieldTokens(book).some((field) => phrasePrefixMatch(field, q));
}

export function searchBooks(books: readonly BookSummary[], query: string): BookSummary[] {
  if (!query.trim()) return [...books];
  return books.filter((b) => matchesQuery(b, query));
}
